import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import { X, MapPin, Building2, Search, Check } from 'lucide-react';

interface ClubSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (clubId: string, clubName: string) => void;
  clubs: any[];
  primaryColor?: string;
  initialSelection?: string;
}

export function ClubSelectionModal({ 
  isOpen, 
  onClose, 
  onConfirm, 
  clubs = [], 
  primaryColor = '#eab308',
  initialSelection = 'indeterminado'
}: ClubSelectionModalProps) {
  const [selectionType, setSelectionType] = useState<'indeterminado' | 'elegir'>('indeterminado');
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');

  // Handle opening and setting initial state
  React.useEffect(() => {
    if (isOpen) {
      if (initialSelection && initialSelection !== 'indeterminado') {
        setSelectionType('elegir');
        setSelectedClubId(initialSelection);
      } else {
        setSelectionType('indeterminado');
        setSelectedClubId('');
      }
    }
  }, [isOpen, initialSelection]);

  if (typeof document === 'undefined') return null;

  const filteredClubs = clubs.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleConfirm = () => {
    if (selectionType === 'indeterminado') {
      onConfirm('indeterminado', 'A confirmar');
    } else {
      const club = clubs.find(c => c.uid === selectedClubId);
      if (club) {
        onConfirm(club.uid, club.name);
      }
    }
  };

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 15 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 15 }}
            className="bg-[#1e2538] w-full max-w-sm rounded-3xl border border-white/10 overflow-hidden flex flex-col max-h-[85vh] shadow-2xl relative"
          >
            {/* Header */}
            <div className="p-5 pb-3 border-b border-white/5 flex flex-col gap-1 text-center relative shrink-0">
              <button
                onClick={onClose}
                className="absolute right-4 top-4 p-1.5 bg-white/5 hover:bg-white/10 rounded-full transition-colors text-white/50 hover:text-white"
              >
                <X size={16} />
              </button>
              <div className="mx-auto w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center mb-2">
                <MapPin size={18} className="text-white/70" />
              </div>
              <h3 className="text-lg font-black uppercase tracking-wider text-white">¿Dónde se jugará?</h3>
              <p className="text-xs text-white/50">Selecciona el club o decide más tarde</p>
            </div>

            {/* Content */}
            <div className="p-5 flex-1 overflow-y-auto custom-scrollbar flex flex-col gap-4">
              {/* Option 1: Indeterminado */}
              <button
                onClick={() => setSelectionType('indeterminado')}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  selectionType === 'indeterminado' 
                    ? `border-[${primaryColor}] bg-[${primaryColor}]/10` 
                    : 'border-white/5 bg-white/5 hover:bg-white/10'
                }`}
                style={selectionType === 'indeterminado' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectionType === 'indeterminado' ? `border-[${primaryColor}]` : 'border-white/30'
                }`}
                style={selectionType === 'indeterminado' ? { borderColor: primaryColor } : {}}
                >
                  {selectionType === 'indeterminado' && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />}
                </div>
                <span className={`font-bold text-sm ${selectionType === 'indeterminado' ? 'text-white' : 'text-white/70'}`}>
                  Aún no lo sé (A confirmar)
                </span>
              </button>

              {/* Option 2: Elegir club */}
              <button
                onClick={() => setSelectionType('elegir')}
                className={`w-full p-4 rounded-xl border-2 transition-all flex items-center gap-3 ${
                  selectionType === 'elegir' 
                    ? `border-[${primaryColor}] bg-[${primaryColor}]/10` 
                    : 'border-white/5 bg-white/5 hover:bg-white/10'
                }`}
                style={selectionType === 'elegir' ? { borderColor: primaryColor, backgroundColor: `${primaryColor}15` } : {}}
              >
                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                  selectionType === 'elegir' ? `border-[${primaryColor}]` : 'border-white/30'
                }`}
                style={selectionType === 'elegir' ? { borderColor: primaryColor } : {}}
                >
                  {selectionType === 'elegir' && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primaryColor }} />}
                </div>
                <div className="flex flex-col items-start">
                  <span className={`font-bold text-sm ${selectionType === 'elegir' ? 'text-white' : 'text-white/70'}`}>
                    Elegir club ahora
                  </span>
                </div>
              </button>

              {/* Club List (shows only if Elegir is selected) */}
              <AnimatePresence>
                {selectionType === 'elegir' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="flex flex-col gap-3 overflow-hidden"
                  >
                    <div className="relative">
                      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                      <input
                        type="text"
                        placeholder="Buscar club..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-black/40 border border-white/10 rounded-lg pl-9 pr-3 py-2.5 text-xs text-white focus:outline-none focus:border-white/30"
                      />
                    </div>

                    <div className="flex flex-col gap-2 max-h-48 overflow-y-auto custom-scrollbar pr-1">
                      {filteredClubs.length > 0 ? (
                        filteredClubs.map(club => (
                          <button
                            key={club.uid}
                            onClick={() => setSelectedClubId(club.uid)}
                            className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all text-left ${
                              selectedClubId === club.uid
                                ? `border-[${primaryColor}] bg-[${primaryColor}]/5`
                                : 'border-white/5 bg-black/20 hover:bg-white/5'
                            }`}
                            style={selectedClubId === club.uid ? { borderColor: primaryColor, backgroundColor: `${primaryColor}10` } : {}}
                          >
                            <div className="flex items-center gap-3 overflow-hidden">
                              {club.photoUrl ? (
                                <img src={club.photoUrl} alt={club.name} className="w-8 h-8 rounded-full object-cover shrink-0 bg-white/10" />
                              ) : (
                                <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                                  <Building2 size={14} className="text-white/40" />
                                </div>
                              )}
                              <span className={`text-sm font-bold truncate ${selectedClubId === club.uid ? 'text-white' : 'text-white/80'}`}>
                                {club.name}
                              </span>
                            </div>
                            {selectedClubId === club.uid && (
                              <Check size={16} style={{ color: primaryColor }} className="shrink-0 ml-2" />
                            )}
                          </button>
                        ))
                      ) : (
                        <div className="text-center py-4 text-xs text-white/40">
                          No se encontraron clubes
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-white/5 shrink-0 bg-black/20">
              <button
                onClick={handleConfirm}
                disabled={selectionType === 'elegir' && !selectedClubId}
                style={{ backgroundColor: primaryColor }}
                className="w-full py-3.5 rounded-xl text-black font-black uppercase tracking-wider text-sm hover:scale-[1.02] active:scale-[0.98] transition-all disabled:opacity-50 disabled:scale-100 shadow-[0_0_20px_rgba(163,230,53,0.3)]"
              >
                Confirmar
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
